import type { LoanEntry, LoanPreviewSummary } from './types'

export type LoanInputs = {
  loanAmount: number
  annualRate: number // e.g. 5.5 for 5.5%
  loanYears: number
  paymentsPerYear: number
  startDate: string // ISO YYYY-MM-DD
}

/**
 * Standard amortization: M = P × r(1+r)^n / ((1+r)^n − 1).
 * Mirrors frontend/public/loan_amortization.html calculatePreview.
 */
export function buildLoanSchedule(inputs: LoanInputs): {
  entries: LoanEntry[]
  summary: LoanPreviewSummary
} {
  const { loanAmount, annualRate, loanYears, paymentsPerYear, startDate } = inputs
  if (
    !(loanAmount > 0) ||
    !(annualRate > 0) ||
    !(loanYears > 0) ||
    !(paymentsPerYear > 0)
  ) {
    return {
      entries: [],
      summary: { scheduledPayment: 0, totalPayments: 0, totalInterest: 0, totalCost: 0 },
    }
  }

  const periodicRate = annualRate / 100 / paymentsPerYear
  const totalPayments = Math.round(loanYears * paymentsPerYear)
  const growth = Math.pow(1 + periodicRate, totalPayments)
  const payment = (loanAmount * (periodicRate * growth)) / (growth - 1)
  const scheduledPayment = isFinite(payment) ? payment : 0

  const entries: LoanEntry[] = []
  let beginBal = loanAmount
  let cumInterest = 0
  const start = new Date(startDate + 'T00:00:00')

  for (let i = 1; i <= totalPayments; i++) {
    const interest = beginBal * periodicRate
    const principal = scheduledPayment - interest
    const endBal = i === totalPayments ? 0 : beginBal - principal
    cumInterest += interest

    const d = new Date(start)
    d.setMonth(start.getMonth() + i - 1)

    entries.push({
      period_number: i,
      entry_date: d.toISOString().slice(0, 10),
      amount: round(principal),
      secondary_amount: round(interest),
      balance: round(endBal),
      accumulated: round(cumInterest),
    })

    beginBal = endBal
  }

  return {
    entries,
    summary: {
      scheduledPayment: round(scheduledPayment),
      totalPayments,
      totalInterest: round(cumInterest),
      totalCost: round(loanAmount + cumInterest),
    },
  }
}

function round(n: number): number {
  return Math.round(n * 100) / 100
}
